import { useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Layout } from '~/eds/src/components/Layout/Layout';
import { LoadingSpinner } from '~/eds/src/components/LoadingSpinner/LoadingSpinner';
import { SearchInput } from '~/eds/src/components/SearchInput/SearchInput';
import { useDebouncedValue } from '~/eds/src/hooks/useDebouncedValue';
import { SearchParams } from '~/redux/api/methods/evisearch';
import { BatchAddGroupNodeItem } from '~/redux/api/methods/group-nodes/types';

import { useSearchType } from '../hooks/useSearchForType';
import { AddItemsResults } from './AddItemsResults';

const messages = defineMessages({
  searchPlaceholder: {
    id: 'AddItemsPanel.searchPlaceholder',
    defaultMessage: 'Search for documents or tickets...',
    description: 'Placeholder for search input',
  },
});

const SEARCH_DEBOUNCE_MS = 300;

const EXCLUDE_FINALIZE_STAGE: SearchParams['query'] = [
  {
    type: 'operator',
    value: 'and',
  },
  {
    type: 'filter',
    id: 'stage_Ticket',
    operator: 'not_equals',
    value: {
      value_list: ['complete'],
    },
  },
];

export interface SelectItemsStepProps {
  selectedIds: string[];
  isVisible: boolean;
  onSelectionChange: (items: BatchAddGroupNodeItem[]) => void;
}

export const useSearchDocsAndTickets = (searchQuery: string) => {
  const debouncedQuery = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS);

  const {
    rows: documents,
    isLoading: isLoadingDocuments,
    hasMore: hasMoreDocuments,
    loadMore: loadMoreDocuments,
  } = useSearchType({
    query: debouncedQuery,
    type: 'document',
    skip: !debouncedQuery,
  });

  const {
    rows: tickets,
    isLoading: isLoadingTickets,
    hasMore: hasMoreTickets,
    loadMore: loadMoreTickets,
  } = useSearchType({
    query: debouncedQuery,
    type: 'ticket',
    skip: !debouncedQuery,
    additionalQueryFilters: EXCLUDE_FINALIZE_STAGE,
  });

  const isLoading = isLoadingDocuments || isLoadingTickets;
  const hasResults = documents.length > 0 || tickets.length > 0;
  const isInitialLoading = isLoading && !hasResults;
  const isLoadingMore = isLoading && hasResults;
  const hasMore = hasMoreDocuments || hasMoreTickets;

  const loadMore = useCallback(() => {
    if (isLoading || !hasMore) return;
    if (hasMoreDocuments) loadMoreDocuments();
    if (hasMoreTickets) loadMoreTickets();
  }, [
    isLoading,
    hasMore,
    hasMoreDocuments,
    hasMoreTickets,
    loadMoreDocuments,
    loadMoreTickets,
  ]);

  return {
    isLoading: isInitialLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    documents,
    tickets,
  };
};

export const SelectItemsStep = ({
  isVisible,
  selectedIds,
  onSelectionChange,
}: SelectItemsStepProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { formatMessage } = useIntl();

  const handleSearch = (value: string | null) => {
    setSearchQuery(value ?? '');
  };

  const loadMoreRef = useRef(() => {});
  const { isLoading, hasMore, loadMore, documents, tickets } =
    useSearchDocsAndTickets(searchQuery);

  // use a ref to avoid disconnecting/creating intersection observer on every render
  loadMoreRef.current = () => {
    // since we're using a debounced search query, we don't want to load more if the query becomes empty
    // but the debounced query is still updating
    if (!searchQuery) return;
    loadMore();
  };

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const scrollContainer = scrollContainerRef.current;
    if (!sentinel || !scrollContainer) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreRef.current();
        }
      },
      { root: scrollContainer, rootMargin: '0px 0px 20px 0px' },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore]);

  return (
    <Layout
      direction="column"
      flex="1 1 0"
      justify="stretch"
      maxHeight="100%"
      display={isVisible ? 'flex' : 'none'}
    >
      <SearchInput
        name="item-search"
        placeholder={formatMessage(messages.searchPlaceholder)}
        value={searchQuery}
        onChange={handleSearch}
      />
      <div ref={scrollContainerRef} style={{ flex: '1 1 0', overflow: 'auto' }}>
        <AddItemsResults
          searchQuery={searchQuery}
          isLoading={isLoading}
          documents={documents}
          tickets={tickets}
          selectedRowIds={selectedIds}
          onSelectionChange={onSelectionChange}
        />
        {hasMore && (
          <div ref={sentinelRef}>
            <Layout justify="center" py={4}>
              <LoadingSpinner />
            </Layout>
          </div>
        )}
      </div>
    </Layout>
  );
};
